/**
 * property-path.ts - Property path extraction with wildcard support
 *
 * Extends lodash.get() to support [*] wildcard syntax for arrays.
 *
 * @module property-path
 */

import { get } from 'lodash-es';

/**
 * Enhanced property path getter with wildcard support.
 * Extends lodash.get() to support [*] wildcard for arrays.
 *
 * Supports:
 * - Simple paths: "timeRange.start" → value
 * - Wildcard syntax: "assignees[*].uid" → array of values
 * - Nested wildcards: "teams[*].members[*].id" → flattened array
 * - Empty arrays: "assignees[*].uid" with assignees=[] → []
 * - Non-arrays: "assignees[*].uid" with assignees=undefined → undefined
 *
 * @example
 * getWithWildcard(item, "assignees[*].uid")  // ["alice", "bob"]
 * getWithWildcard(item, "timeRange.start")   // DateTime
 * getWithWildcard(item, "eventDate")         // DateTime
 * getWithWildcard(item, "teams[*].members[*].id") // Nested → flattened
 *
 * @param obj Source object
 * @param path Property path (lodash.get() syntax + [*] wildcard)
 * @returns Extracted value (single value or array)
 * @throws Error if wildcard used on non-array
 */
export function getWithWildcard(obj: any, path: string): any {
  // Check for [*] wildcard
  if (!path.includes('[*]')) {
    // No wildcard → delegate to lodash.get()
    return get(obj, path);
  }

  // Split path at FIRST [*] occurrence
  const wildcardIndex = path.indexOf('[*]');
  const beforeWildcard = path.substring(0, wildcardIndex);
  const afterWildcard = path.substring(wildcardIndex + 3); // Skip '[*]'

  // Extract array at wildcard position
  const array = beforeWildcard ? get(obj, beforeWildcard) : obj;

  // Edge-Case 1: Empty path before wildcard (invalid)
  if (beforeWildcard === '' && !Array.isArray(obj)) {
    throw new Error(`Wildcard [*] at root requires array, got ${typeof obj}`);
  }

  // Edge-Case 2: Undefined array (property doesn't exist)
  if (array === undefined || array === null) {
    return undefined;
  }

  // Edge-Case 3: Non-array at wildcard position
  if (!Array.isArray(array)) {
    throw new Error(
      `Wildcard [*] expects array at path '${beforeWildcard}', got ${typeof array}`
    );
  }

  // Edge-Case 4: Empty array
  if (array.length === 0) {
    return [];
  }

  // Remove leading dot from afterWildcard if present
  const restPath = afterWildcard.replace(/^\./, '');

  // Map over array and apply rest of path recursively
  if (restPath) {
    // Nested wildcard or property path after [*]
    const results = array.map(item => getWithWildcard(item, restPath));

    // Flatten if nested wildcard returned arrays
    return results.flat();
  } else {
    // No path after [*] → return array items directly
    return array;
  }
}
